import { StepName } from '|/app/services';

export interface ChatSurface {
  surface: string;
  surface_description: string;
}

interface ChatSurfaceMapping extends ChatSurface {
  slateSurfaceDescription?: string;
}

const KIQ: ChatSurface = {
  surface: 'CHECKOUT_VERIFICATION_KIQ',
  surface_description: 'Customer is answering knowledge-based identity verification questions'
};
const BAV: ChatSurface = {
  surface: 'CHECKOUT_VERIFICATION_BAV',
  surface_description: 'Customer is verifying their bank account details for payment processing'
};
const INCOME: ChatSurface = {
  surface: 'CHECKOUT_VERIFICATION_INCOME',
  surface_description: 'Customer is providing income documentation for financing verification'
};
const ADDRESS: ChatSurface = {
  surface: 'CHECKOUT_VERIFICATION_ADDRESS',
  surface_description:
    'Customer is confirming or updating their address for delivery and registration'
};
const TRADE: ChatSurface = {
  surface: 'CHECKOUT_VERIFICATION_TRADE',
  surface_description: 'Customer is completing verification steps for their trade-in vehicle'
};
const ADDITIONAL_INCOME: ChatSurface = {
  surface: 'CHECKOUT_VERIFICATION_ADDITIONAL_INCOME',
  surface_description:
    'Customer is providing additional income documentation required for high-risk financing approval'
};

export const CHAT_SURFACE_MAPPINGS: Record<string, ChatSurfaceMapping> = {
  'down-payment': {
    surface: 'CHECKOUT_DOWN_PAYMENT',
    surface_description:
      'Customer provides payment information (bank account or payment card) for down payment and/or cash due at delivery. No payment is collected on this step—only connecting a payment method.'
  },
  'autopay-enrollment': {
    surface: 'CHECKOUT_AUTOPAY_ENROLLMENT',
    surface_description:
      'Customer sets up automatic monthly payments by providing bank account information.'
  },
  'buyer-identity': {
    surface: 'CHECKOUT_BUYER_IDENTITY',
    surface_description:
      "Customer uploads front and back photos of their driver's license and provides their Social Security Number for identity verification"
  },
  'co-signer-identity': {
    surface: 'CHECKOUT_CO_SIGNER_IDENTITY',
    surface_description:
      "Co-signer uploads front and back photos of their driver's license and provides their Social Security Number for identity verification"
  },
  'drivers-license': {
    surface: 'CHECKOUT_DRIVERS_LICENSE',
    surface_description:
      "Customer uploads front and back photos of their driver's license (and co-buyer's if applicable)"
  },
  identity: {
    surface: 'CHECKOUT_IDENTITY',
    surface_description:
      "Customer provides Social Security Number for identity verification (and co-buyer's if applicable)"
  },
  _verification: {
    surface: 'CHECKOUT_VERIFICATION',
    surface_description:
      'Customer is completing a verification step for their purchase, such as identity, bank account, income, address, or trade-in verification'
  },
  'transfer-select-slot': {
    surface: 'CHECKOUT_TRANSFER_SELECT_SLOT',
    surface_description: 'Customer is scheduling their vehicle delivery or pickup date and time'
  },
  'transfer-schedule': {
    surface: 'CHECKOUT_TRANSFER_SCHEDULE',
    surface_description: 'Customer is scheduling their vehicle delivery or pickup date and time'
  },
  'review-order': {
    surface: 'CHECKOUT_REVIEW_ORDER',
    surface_description:
      'Customer reviews complete order (vehicle, pricing, protection, financing, delivery), agrees to terms, and submits order.'
  },
  'complete-purchase': {
    surface: 'CHECKOUT_COMPLETE_PURCHASE',
    surface_description:
      'Customer reviews complete order (vehicle, pricing, protection, financing, delivery), agrees to terms, and submits order.'
  },
  'order-placed': {
    surface: 'CHECKOUT_ORDER_PLACED',
    surface_description:
      'Customer has submitted their order and is viewing the order confirmation, including delivery or pickup appointment details, amounts due, and any remaining tasks to complete before receiving the vehicle.'
  },
  'insurance-options': {
    surface: 'CHECKOUT_INSURANCE',
    surface_description:
      'Customer reviews auto insurance options and selects coverage or provides proof of insurance for their vehicle.'
  },

  'identity-verification': KIQ,
  bav: BAV,
  'bank-account-verification': BAV,
  income: INCOME,
  'income-verification': INCOME,
  'income-information': INCOME,
  address: ADDRESS,
  'address-verification': ADDRESS,
  'residency-information': ADDRESS,
  'trade-verification': TRADE,
  'trade-documentation': TRADE,
  'additional-income': ADDITIONAL_INCOME,

  [StepName.PersonalDetails]: {
    surface: 'CART_PERSONAL_DETAILS',
    surface_description:
      'Customer enters name, address, phone, military status, home ownership, marital status'
  },
  [StepName.TradeIn]: {
    surface: 'CART_TRADE_IN',
    surface_description:
      'Customer decides whether to trade in their vehicle, provides details, uploads photos, receives appraisal. If customers add or remove a trade in from their purchase, their terms can change.'
  },
  [StepName.Accessories]: {
    surface: 'CART_ACCESSORIES',
    surface_description: 'Customer chooses how their accessories are installed.'
  },
  [StepName.CoF]: {
    surface: 'CART_PAYMENT_OPTIONS',
    surface_description:
      'Customer chooses cash payment or financing. If financing, they select Carvana financing (build payment plan) or self-arranged financing (provide lender info).',
    slateSurfaceDescription:
      'Customer chooses cash payment or financing. If financing, they select Slate financing (build payment plan) or self-arranged financing (provide lender info).'
  },
  [StepName.Gap]: {
    surface: 'CART_GAP_COVERAGE',
    surface_description:
      'Customer reviews gap waiver options that cover loan balance vs. vehicle value in total loss scenarios'
  },
  [StepName.PickupDelivery]: {
    surface: 'CART_TRANSFER_METHOD',
    surface_description:
      'Customer selects home delivery, pickup at location, or vending machine pickup, and confirms address'
  },
  [StepName.VSC]: {
    surface: 'CART_CARVANA_PROTECTION',
    surface_description:
      'Customer reviews CarvanaCare vehicle service contract options and selects coverage level or declines'
  },
  [StepName.CleanVehicleTaxCredit]: {
    surface: 'CART_CLEAN_VEHICLE_TAX_CREDIT',
    surface_description:
      'Customer is reviewing clean vehicle tax credit eligibility for their purchase'
  },
  [StepName.Incentives]: {
    surface: 'CART_INCENTIVES',
    surface_description:
      'Customer checks eligibility for manufacturer incentives by providing occupation, employer, or membership info'
  }
};

export function getChatSurface(stepUrl: string, isSlate = false): ChatSurface | null {
  const mapping = CHAT_SURFACE_MAPPINGS[stepUrl];
  if (!mapping) return null;
  return {
    surface: mapping.surface,
    surface_description:
      isSlate && mapping.slateSurfaceDescription
        ? mapping.slateSurfaceDescription
        : mapping.surface_description
  };
}
